import { Checkbox, Form } from 'components/utils/form/';
import Icon from 'components/utils/Icon';
import Pagination from 'components/utils/Pagination';
import Loader from 'components/utils/Spinner';
import useTranslation from 'hooks/useTranslation';
import React, { useCallback } from 'react';
import { useState } from 'react';
import { isEmpty } from 'tools/array';
import { getValueByIndex, mergeRows } from 'tools/utils';

import Popover from '../Popover';

function Table({
  rowKey = '_id',
  columns,
  optionalColumns = [],
  data = [],
  onChange,
  rowSelection,
  loading,
  onRowClick,
  className,
  activeColumns = columns || [],
  setActiveColumns = () => {},
  selectedRows = [],
  isRowSelected = () => {},
  isAllSelected = false,
  currentPage = 0,
  totalPages = 0,
}) {
  const { TRANSLATIONS } = useTranslation();
  const [clickedRow, setClickedRow] = useState();

  const selectedRowKeys = selectedRows.map(
    (selectedRows) => selectedRows[rowKey]
  );
  const allColumns = [...columns, ...optionalColumns].map((c, index) => ({
    ...c,
    order: index,
  }));

  function onPaginationChange(page) {
    onChange(page);
  }

  function handleSelectRows(event, record) {
    const set = new Set(selectedRowKeys);
    let rows;
    if (event.target.checked) {
      set.add(record[rowKey]);
      rows = [...selectedRows, record];
    } else {
      set.delete(record[rowKey]);
      rows = selectedRows.filter((r) => r[rowKey] !== record[rowKey]);
    }

    const keys = [...set];

    rowSelection.onChange(keys, rows, activeColumns, record);
  }

  function handleSelectAll(event) {
    const { rows } = mergeRows(
      event.target.checked,
      data,
      selectedRows,
      rowKey
    );

    rowSelection.onChangeAll(rows);
  }

  const handleRowClick = useCallback(
    (record) => {
      if (onRowClick) {
        onRowClick(record);
        if (
          clickedRow === record[rowKey] ||
          record?.merchantChildren?.findIndex(
            (child) => child?._id === clickedRow
          ) !== -1
        ) {
          setClickedRow(null);
        } else {
          setClickedRow(record[rowKey]);
        }
      }
    },
    [clickedRow, onRowClick, rowKey]
  );

  const isActiveColumn = useCallback(
    (key) => !!activeColumns.find((c) => c.key === key),
    [activeColumns]
  );

  function changeActiveColumn(el) {
    const column = allColumns.find((c) => c.key === el.target.name);

    if (column) {
      let columns = el.target.checked
        ? [...activeColumns, column]
        : [...activeColumns.filter((c) => c.key !== column.key)];

      setActiveColumns(columns.sort((a, b) => a.order - b.order));
    }
  }

  const isSelectedFamily = useCallback(
    (record) => {
      const isChildrenSelected = record?.merchantChildren?.findIndex(
        (child) => child._id === clickedRow
      );

      return !!(
        record[rowKey] === clickedRow ||
        record?.merchantParent ||
        isChildrenSelected !== -1
      );
    },
    [clickedRow, rowKey]
  );

  const isSelected = useCallback(
    (record) => record[rowKey] === clickedRow,
    [clickedRow, rowKey]
  );

  const CustomizeContent = (
    <div className="columns-list">
      <Form>
        <div className="items">
          {allColumns.map(
            (c) =>
              !c.excludeOptional && (
                <div className="item" key={c?.key}>
                  <Checkbox
                    label={c.title}
                    name={c.key}
                    checked={isActiveColumn(c.key)}
                    onChange={changeActiveColumn}
                  />
                </div>
              )
          )}
        </div>
      </Form>
    </div>
  );
  return (
    <div>
      {loading && <Loader />}

      {!isEmpty(optionalColumns) && (
        <div className="customizable">
          <Popover content={CustomizeContent}>
            <div className="customize-link">
              <Icon type="plus-square" />
            </div>
          </Popover>
        </div>
      )}
      <div>
        <div className={className}>
          <table border="1">
            <thead>
              <tr>
                {rowSelection && (
                  <th>
                    <input
                      onChange={handleSelectAll}
                      checked={isAllSelected}
                      type="checkbox"
                    />
                  </th>
                )}
                {activeColumns.map(({ key, title }) => (
                  <th scope="col" key={key}>
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && !!data?.length === 0 ? (
                <tr>
                  <td className="empty-table">
                    <Icon
                      type="hard-drive"
                      style={{
                        fontSize: 60,
                        padding: '5px 0px',
                        opacity: '0.4',
                      }}
                    />
                    <p>{TRANSLATIONS.orders.list.noData}</p>
                  </td>
                </tr>
              ) : (
                data?.map((record) => (
                  <tr
                    key={record[rowKey]}
                    className={`${
                      isSelectedFamily(record) && 'selected-family'
                    } ${isSelected(record) && 'clicked'}`}
                  >
                    {rowSelection && (
                      <td>
                        {record?.merchantParent ? (
                          <div />
                        ) : (
                          <div className="d-f fd-r">
                            <input
                              checked={isRowSelected(record)}
                              onChange={(e) => handleSelectRows(e, record)}
                              type="checkbox"
                            />
                            {record?.merchantChildren?.length ? (
                              <div
                                onClick={() => handleRowClick(record)}
                                className="ml-md"
                              >
                                {isSelectedFamily(record) ? (
                                  <Icon type="chevron-up" />
                                ) : (
                                  <Icon type="chevron-down" />
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </td>
                    )}
                    {activeColumns.map(({ key, render, title }, index) => (
                      <td
                        key={key}
                        data-label={key}
                        data-title={title}
                        onClick={() => handleRowClick(record)}
                      >
                        {render
                          ? render(getValueByIndex(record, key), record, index)
                          : getValueByIndex(record, key)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!!currentPage && (
          <>
            {!!(data?.length != 0) && (
              <Pagination
                data={{ currentPage, totalPages }}
                onChange={onPaginationChange}
              />
            )}
          </>
        )}
      </div>

      <style jsx>{`
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          font-weight: 700;
        }
        .empty-table {
          position: absolute;
          width: 100%;
          text-align: center;
          height: 150px;
          display: flex;
          align-items: center;
          flex-direction: column;
          justify-content: center;
          border: none;
          cursor: auto;
        }
      `}</style>
    </div>
  );
}

export default Table;
